import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DataService } from "../data.service";
import { PracticePriceReservation } from "../../model/practice-price-reservation";
import { Reservation } from "../../model/reservation";
import { ValorizationAmount } from "../../model/valorization";
import { ValorizationEdit } from "../../model/valorization-edit";
import { PracticeRegionSearchReservation } from "../../model/practice-region-reservation";
import { PracticeService } from "../practice.service";
import { ValorizationItemAmount } from "../../model/valorization-item";
import { AgreementPriceConfiguration } from "../../model/agreement-price-configuration";
import { PracticePriceReservationResponse } from "../../model/practice-price-reservation-response";
import { BillingValorization } from "../../model/billing/billing-order-control";
import { MedicalFeeControlSearchItem } from "../../model/medical-fee/medical-fee-control-search-item";
import {
  AdmissionValorization,
  ValorizationValidation,
} from "src/app/model/admission";

@Injectable()
export class ValorizationService {
  constructor(
    private dataService: DataService,
    private practiceService: PracticeService
  ) {}

  private readonly getValorizacionValidationEndPoint: string =
    "/admission/GetValorizacionValidation/";
  private readonly getAgreementPriceConfigurationEndPoint: string =
    "/valorization/getAgreementPriceConfiguration/";
  private readonly refreshItemInValorizationEditEndPoint: string =
    "/valorization/refreshItemInValorizationEdit/";
  private readonly refreshItemInValorizationEditToParticularEndPoint: string =
    "/valorization/refreshItemInValorizationEditToParticular/";
  private readonly addNewPracticeInValorizationEditEndPoint: string =
    "/valorization/addNewPracticeInValorizationEdit/";

  private readonly addNewPracticeInValorizationEditFromAgreementPriceIdsEndPoint: string =
    "/valorization/addNewPracticeInValorizationEditFromAgreementPriceIds/";

  private readonly practicePriceReservationEndPoint: string =
    "/valorization/practicePriceReservation/";
  private readonly getNewAdmissionValorizationEndPoint: string =
    "/valorization/getNewAdmissionValorization/";
  private readonly getAdmissionValorizationEndPoint: string =
    "/valorization/getAdmissionValorization/";
  private readonly recalculateAdmissionValorizationEndPoint: string =
    "/valorization/recalculateAdmissionValorization/";
  private readonly recalculateBillingValorizationEndPoint: string =
    "/valorization/recalculateBillingValorization/";
  private readonly getBillingValorizationEndPoint: string =
    "/valorization/getBillingValorization/";
  private readonly saveValorizationEndPoint: string =
    "/valorization/saveValorization/";
  private readonly recalculateMedicalFeeBillingValorizationEndPoint: string =
    "/valorization/recalculateMedicalFeeBillingValorization/";
  private readonly getNewAdmissionValorizationIntegratedProcedureEndPoint: string =
    "/valorization/getNewAdmissionValorizationIntegratedProcedure/";
  private readonly addItemValorizationIntegratedProcedureEndPoint: string =
    "/valorization/addItemValorizationIntegratedProcedure/";
  private readonly recalculateValorizationIntegratedProcedureEndPoint: string =
    "/valorization/recalculateValorizationIntegratedProcedure/";
  private readonly refreshItemInValorizationEditIntegratedProcedureEndPoint: string =
    "/valorization/RefreshItemInValorizationEditIntegratedProcedure/";
  private readonly editValidateValorizationEndPoint: string =
    "/valorization/editValidateValorization/";

  async editValidateValorization(valorizationId: number): Promise<boolean> {
    const param = {
      ValorizationId: valorizationId,
    };
    return await this.dataService
      .post<boolean>(this.editValidateValorizationEndPoint, param)
      .toPromise();
  }

  async recalculateValorizationIntegratedProcedure(
    valorizationEdit: ValorizationEdit,
    date: Date,
    equipmentId: number,
    currentPlanId?: number,
    affectParticular: boolean = false
  ): Promise<ValorizationEdit> {
    const param = {
      ValorizationEdit: valorizationEdit,
      equipmentId,
      date: date,
      affectParticular,
      currentPlanId,
    };
    return await this.dataService
      .post<ValorizationEdit>(
        this.recalculateValorizationIntegratedProcedureEndPoint,
        param
      )
      .toPromise();
  }

  async refreshItemInValorizationEditIntegratedProcedure(
    valorizationEdit: ValorizationEdit,
    valorizationItemAmount: ValorizationItemAmount,
    date: Date,
    equipmentId: number,
    toParticular: boolean = false
  ): Promise<ValorizationEdit> {
    const param = {
      ValorizationEdit: valorizationEdit,
      ValorizationItemAmount: valorizationItemAmount,
      equipmentId,
      date: date,
      toParticular,
    };
    return await this.dataService
      .post<ValorizationEdit>(
        this.refreshItemInValorizationEditIntegratedProcedureEndPoint,
        param
      )
      .toPromise();
  }

  async addItemValorizationIntegratedProcedure(
    valorizationEdit: ValorizationEdit,
    date: Date,
    planId: number,
    equipmentId: number,
    procedureId: number,
    centerId?: number,
    physicianId?: number
  ): Promise<ValorizationEdit> {
    const param = {
      valorizationEdit: valorizationEdit,
      date: date,
      planId: planId,
      equipmentId: equipmentId,
      procedureId: procedureId,
      centerId: centerId,
      physicianId: physicianId,
    };

    return await this.dataService
      .post<ValorizationEdit>(
        this.addItemValorizationIntegratedProcedureEndPoint,
        param
      )
      .toPromise();
  }

  async getNewAdmissionValorizationIntegratedProcedure(
    date: Date,
    planId: number,
    equipmentId: number,
    procedureIds: number[],
    centerId?: number
  ): Promise<AdmissionValorization> {
    const param = {
      planId: planId,
      equipmentId: equipmentId,
      procedureIds: procedureIds,
      centerId: centerId,
      date: date,
    };

    return await this.dataService
      .post<AdmissionValorization>(
        this.getNewAdmissionValorizationIntegratedProcedureEndPoint,
        param
      )
      .toPromise();
  }

  async saveValorization(valorizationEdit: ValorizationEdit): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.saveValorizationEndPoint, valorizationEdit)
      .toPromise();
  }

  async getAgreementPriceConfiguration(
    agreementPriceId: number
  ): Promise<AgreementPriceConfiguration> {
    const param = { agreementPriceId: agreementPriceId };
    return await this.dataService
      .get<AgreementPriceConfiguration>(
        this.getAgreementPriceConfigurationEndPoint,
        param
      )
      .toPromise();
  }

  async refreshItemInValorizationEdit(
    valorizationEdit: ValorizationEdit,
    valorizationItemAmount: ValorizationItemAmount
  ): Promise<ValorizationEdit> {
    const param = {
      ValorizationEdit: valorizationEdit,
      ValorizationItemAmount: valorizationItemAmount,
    };
    return await this.dataService
      .post<ValorizationEdit>(this.refreshItemInValorizationEditEndPoint, param)
      .toPromise();
  }

  async refreshItemInValorizationEditToParticular(
    valorizationEdit: ValorizationEdit,
    valorizationItemAmount: ValorizationItemAmount
  ): Promise<ValorizationEdit> {
    const param = {
      ValorizationEdit: valorizationEdit,
      ValorizationItemAmount: valorizationItemAmount,
    };
    return await this.dataService
      .post<ValorizationEdit>(
        this.refreshItemInValorizationEditToParticularEndPoint,
        param
      )
      .toPromise();
  }

  async addNewPracticeInValorizationEdit(
    valorizationEdit: ValorizationEdit
  ): Promise<ValorizationEdit> {
    return await this.dataService
      .post<ValorizationEdit>(
        this.addNewPracticeInValorizationEditEndPoint,
        valorizationEdit
      )
      .toPromise();
  }

  async addNewPracticeInValorizationEditFromAgreementPriceIds(
    valorizationEdit: ValorizationEdit
  ): Promise<ValorizationEdit> {
    return await this.dataService
      .post<ValorizationEdit>(
        this.addNewPracticeInValorizationEditFromAgreementPriceIdsEndPoint,
        valorizationEdit
      )
      .toPromise();
  }

  async getValorizacionValidation(
    medicalAppointmentId: number,
    agreementPriceIds: number[]
  ): Promise<ValorizationValidation> {
    return await this.dataService
      .post<ValorizationValidation>(this.getValorizacionValidationEndPoint, {
        MedicalAppointmentId: medicalAppointmentId,
        AgreementPriceIds: agreementPriceIds,
      })
      .toPromise();
  }

  async practicePriceReservation(
    reservation: Reservation
  ): Promise<PracticePriceReservationResponse> {
    return await this.dataService
      .post<PracticePriceReservationResponse>(
        this.practicePriceReservationEndPoint,
        reservation
      )
      .toPromise();
  }

  async recalculateAdmissionValorization(
    admissionValorization: AdmissionValorization
  ): Promise<AdmissionValorization> {
    return await this.dataService
      .post<AdmissionValorization>(
        this.recalculateAdmissionValorizationEndPoint,
        admissionValorization
      )
      .toPromise();
  }

  async recalculateBillingValorization(
    billingValorization: BillingValorization
  ): Promise<BillingValorization> {
    return await this.dataService
      .post<BillingValorization>(
        this.recalculateBillingValorizationEndPoint,
        billingValorization
      )
      .toPromise();
  }

  async recalculateMedicalFeeBillingValorization(
    medicalFeeControlSearchItem: MedicalFeeControlSearchItem
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(
        this.recalculateMedicalFeeBillingValorizationEndPoint,
        medicalFeeControlSearchItem
      )
      .toPromise();
  }

  async getNewAdmissionValorization(
    medicalAppointmentId: number,
    budgetId: number,
    planId: number | null = null
  ): Promise<AdmissionValorization> {
    const param = {
      medicalAppointmentId: medicalAppointmentId,
      budgetId,
      planId,
    };
    return await this.dataService
      .get<AdmissionValorization>(
        this.getNewAdmissionValorizationEndPoint,
        param
      )
      .toPromise();
  }

  async getBillingValorization(
    medicalAppointmentId: number
  ): Promise<BillingValorization> {
    const param = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<BillingValorization>(this.getBillingValorizationEndPoint, param)
      .toPromise();
  }

  async getAdmissionValorization(
    medicalAppointmentId: number
  ): Promise<AdmissionValorization> {
    const param = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<AdmissionValorization>(this.getAdmissionValorizationEndPoint, param)
      .toPromise();
  }

  recalculatePatientTaxAmount(
    valorizationAmount: ValorizationAmount
  ): ValorizationAmount {
    valorizationAmount.ValorizationItemsAmount.forEach((item) => {
      const patientAmount = item.PatientAmount;
      const entityAmount = item.EntityAmount;
      const patientTaxAmount =
        patientAmount *
        (valorizationAmount.Valorization.AliquotTax.Percentage / 100);
      const entityTaxAmount =
        entityAmount *
        (valorizationAmount.Valorization.AliquotTax.Percentage / 100);
      item.PatientTaxAmount = patientTaxAmount;
      item.EntityTaxAmount = entityTaxAmount;
    });
    return valorizationAmount;
  }

  async getPracticesRegionSearchReservationToAdd(
    valorizationAmount: ValorizationAmount
  ): Promise<PracticeRegionSearchReservation[]> {
    const items = valorizationAmount.ValorizationItemsAmount;
    const practiceRegionIds: number[] = [];
    items.forEach((item: ValorizationItemAmount) => {
      if (!item.ValorizationItem.MedicalAppointmentProcedure.SetSupplieId) {
        practiceRegionIds.push(
          !item.ValorizationItem.MedicalAppointmentProcedure
            .PracticeRegionAditionalId
            ? item.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionId
            : item.ValorizationItem.MedicalAppointmentProcedure
                .PracticeRegionAditionalId
        );
      }
    });
    return await this.practiceService.getPosiblePracticeRegionSearchForValorization(
      valorizationAmount.Valorization.PlanId,
      valorizationAmount.Valorization.MedicalAppointmentId,
      practiceRegionIds
    );
  }
}
